<script setup lang="ts">
import { ref } from 'vue';
import { t } from '../i18n';
import { tell } from '../dialogs';
import { useApp } from '../stores/app';
import { unlockProfile } from '../profiles/lock';
import { switchProfile } from '../profiles/session';
import { exportProfile } from '../profiles/transfer';
import { profileState } from '../profiles/state';
import PinForm from './PinForm.vue';
import ProfileDeleteForm from './ProfileDeleteForm.vue';

/** The open profile is locked: the PIN screen over the app (the canvases stay as they are underneath). */
const app = useApp();
const forgot = ref(false);

async function doExport(): Promise<void> {
  try {
    await exportProfile();
  } catch (e) {
    app.notify('error', t('profiles.exportFailed', { msg: e instanceof Error ? e.message : String(e) }));
  }
}

/** A forgotten PIN cannot be reset: the person confirms deletion, then the app starts over. */
async function removed(folderOk: boolean): Promise<void> {
  if (!folderOk) await tell(t('profiles.deletedFolderLeft'));
  window.location.reload();
}
</script>

<template>
  <div v-if="profileState.locked && profileState.current" class="lock-screen" data-profile-lock role="dialog" aria-modal="true" :aria-label="t('profiles.lockedTitle')">
    <div class="float card" :class="{ wide: forgot }">
      <ProfileDeleteForm v-if="forgot" :profile="profileState.current" can-export @export="doExport" @cancel="forgot = false" @done="removed" />
      <PinForm v-else :profile="profileState.current" @ok="unlockProfile">
        <div class="links">
          <button type="button" class="pill" data-act="other-profile" @click="switchProfile">{{ t('profiles.switch') }}</button>
          <button type="button" class="pill" data-act="forgot-pin" @click="forgot = true">{{ t('profiles.forgotPin') }}</button>
        </div>
      </PinForm>
    </div>
  </div>
</template>

<style scoped>
.lock-screen {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--bg);
}
.card {
  width: min(380px, 100%);
  padding: 28px 24px 22px;
  border-radius: var(--radius-panel);
}
.card.wide {
  width: min(460px, 100%);
}
.links {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 200px;
}
.links .pill {
  width: 100%;
  justify-content: center;
}
</style>
